/**
 * VaultCreateFolderModal: Modal for creating folders with visibility, color,
 * and optional user sharing. Replaces the inline folder input.
 */
import { useState, useMemo } from 'react';
import {
  X,
  Folder,
  Globe,
  Lock,
  Users,
  Search,
  Check,
} from 'lucide-react';
import { VaultColorPicker } from './VaultColorPicker';
import type {
  VaultVisibility,
  VaultUser,
  CreateFolderOptions,
} from '../../types/vault.types';
import { logError } from "../../lib/logError";

interface VaultCreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (options: CreateFolderOptions) => Promise<void>;
  parentFolderName?: string | null;
  parentFolderId?: string | null;
  vaultUsers: VaultUser[];
}

export function VaultCreateFolderModal({
  isOpen,
  onClose,
  onCreate,
  parentFolderName,
  parentFolderId,
  vaultUsers,
}: VaultCreateFolderModalProps) {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<VaultVisibility>('public');
  const [color, setColor] = useState('#3b82f6');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return vaultUsers;
    const q = userSearch.toLowerCase();
    return vaultUsers.filter(u =>
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [vaultUsers, userSearch]);

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreate = async () => {
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      await onCreate({
        name: name.trim(),
        parentId: parentFolderId,
        visibility,
        color,
        sharedWith: visibility === 'shared' ? selectedUsers : [],
      });
      // Reset and close
      setName('');
      setVisibility('public');
      setColor('#3b82f6');
      setSelectedUsers([]);
      setUserSearch('');
      onClose();
    } catch (err) {
      logError('[Vault] Failed to create folder:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim()) {
      handleCreate();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const visibilityOptions: { value: VaultVisibility; icon: typeof Globe; label: string; desc: string }[] = [
    { value: 'public', icon: Globe, label: 'Public', desc: 'Visible to all vault users' },
    { value: 'private', icon: Lock, label: 'Private', desc: 'Only visible to you' },
    { value: 'shared', icon: Users, label: 'Shared', desc: 'Share with specific users' },
  ];

  return (
    <div className="vault-modal-overlay" onClick={onClose}>
      <div className="vault-create-folder-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="vault-modal-header">
          <div className="vault-modal-title">
            <Folder size={20} style={{ color }} />
            <h3>New Folder</h3>
          </div>
          <button className="vault-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Location */}
        {parentFolderName && (
          <div className="vault-modal-location">
            Creating inside <strong>{parentFolderName}</strong>
          </div>
        )}

        {/* Name Input */}
        <div className="vault-modal-section">
          <label className="vault-modal-label">Folder Name</label>
          <input
            className="vault-modal-input"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter folder name..."
            maxLength={40}
            autoFocus
          />
          <span className="vault-input-hint">{name.length}/40 characters</span>
        </div>

        {/* Color Picker */}
        <div className="vault-modal-section">
          <label className="vault-modal-label">Color</label>
          <VaultColorPicker selectedColor={color} onSelect={setColor} />
        </div>

        {/* Visibility */}
        <div className="vault-modal-section">
          <label className="vault-modal-label">Visibility</label>
          <div className="vault-visibility-grid">
            {visibilityOptions.map(opt => {
              const Icon = opt.icon;
              const isActive = visibility === opt.value;
              return (
                <button
                  key={opt.value}
                  className={`vault-visibility-card ${isActive ? 'active' : ''}`}
                  onClick={() => setVisibility(opt.value)}
                  type="button"
                >
                  <Icon size={20} />
                  <span className="vault-visibility-label">{opt.label}</span>
                  <span className="vault-visibility-desc">{opt.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* User Picker (shown only when Shared) */}
        {visibility === 'shared' && (
          <div className="vault-modal-section">
            <label className="vault-modal-label">Share With</label>

            {/* Selected user chips */}
            {selectedUsers.length > 0 && (
              <div className="vault-user-chips">
                {selectedUsers.map(uid => {
                  const user = vaultUsers.find(u => u.id === uid);
                  return (
                    <span key={uid} className="vault-user-chip">
                      {user?.full_name || user?.email || 'Unknown'}
                      <button onClick={() => toggleUser(uid)} className="vault-chip-remove" aria-label="Remove user">
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* User search */}
            <div className="vault-user-search-wrap">
              <Search size={14} className="vault-user-search-icon" />
              <input
                className="vault-user-search-input"
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
            </div>

            {/* User list */}
            <div className="vault-user-list">
              {filteredUsers.length === 0 ? (
                <div className="vault-user-empty">No vault users found</div>
              ) : (
                filteredUsers.map(user => {
                  const isSelected = selectedUsers.includes(user.id);
                  return (
                    <button
                      key={user.id}
                      className={`vault-user-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleUser(user.id)}
                      type="button"
                    >
                      <div className="vault-user-avatar">
                        {(user.full_name || user.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="vault-user-info">
                        <span className="vault-user-name">{user.full_name || 'Unnamed'}</span>
                        {user.email && <span className="vault-user-email">{user.email}</span>}
                      </div>
                      {isSelected && <Check size={16} className="vault-user-check" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="vault-modal-footer">
          <button className="vault-modal-btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="vault-modal-btn primary"
            onClick={handleCreate}
            disabled={!name.trim() || creating || (visibility === 'shared' && selectedUsers.length === 0)}
          >
            {creating ? 'Creating...' : 'Create Folder'}
          </button>
        </div>
      </div>
    </div>
  );
}
